import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Content,
  Item,
  Chip,
  ChipContent,
  ChipName,
  ChipEmail,
  EmailSent,
  EmailSentLabel,
  DeleteItem,
  Recipients,
} from './ShareResultsModal.styles';
import SearchDropdown from '../../components/SearchDropdown/SearchDropdown';
import Modal from '../../HOC/Modal/Modal';
import { useShareSurvey } from '../../hooks';
import { IShareUser } from '../../services/surveys';
import CloseMark from '@/assets/icons/closeMark.svg?react';
import SuccessNotificationIcon from '@/assets/icons/successNotificationIcon.svg?react';

interface IShareResultsModal {
  selectedReport?: string;
  title: string;
  closeModal: () => void;
  recipients: any[];
  surveyId: string;
  departmentId: number;
  processReports: () => void;
}

const ShareResultsModal: FC<IShareResultsModal> = ({
  selectedReport,
  closeModal,
  title,
  recipients,
  surveyId,
  departmentId,
  processReports,
}) => {
  const [selectedRecipients, setSelectedRecipients] = useState<any[]>(recipients);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [allItems, setAllItems] = useState<IShareUser[]>([]);
  const [filteredItems, setFilteredItems] = useState<IShareUser[]>([]);
  const { t } = useTranslation();

  const { getSurveyShareUsers, putSurveyShareUsers } = useShareSurvey(surveyId);

  const resetAndCloseModal = () => {
    closeModal();
  };

  const getUsers = async () => {
    const users = await getSurveyShareUsers();
    setAllItems(users);
  };

  useEffect(() => {
    (async () => await getUsers())();
  }, [surveyId]);

  useEffect(() => {
    setSelectedRecipients(recipients);
  }, [recipients]);

  const excludeSelectedRecipients = (rec: IShareUser) =>
    !selectedRecipients.map(rec => rec.id).includes(rec?.id);

  const items = (filteredItems?.filter(excludeSelectedRecipients) || []).map((rec: IShareUser) => ({
    id: rec.id,
    name: (
      <Item data-testid="dropElement">
        <div data-testid="dropElement">{rec.name || '-'}</div>
        <div data-testid="dropElement">{rec.email}</div>
      </Item>
    ),
  }));

  const doFilter = (phrase: string) => {
    setFilteredItems(
      allItems.filter(
        (data: IShareUser) =>
          data.name.toLowerCase().includes(phrase.toLowerCase()) ||
          data.email.toLowerCase().includes(phrase.toLowerCase())
      )
    );
  };

  const selectEl = (item: any) => {
    if (item?.id) {
      setSelectedRecipients([...selectedRecipients, allItems.find(i => i.id === item?.id)]);
    }
  };

  const saveUsers = async () => {
    setSubmitDisabled(true);

    const data = await putSurveyShareUsers(
      departmentId,
      selectedRecipients.map(rec => rec.id)
    );

    if (data) {
      setSubmitDisabled(false);
      (async () => await processReports())();
      resetAndCloseModal();
    }
  };

  return (
    <Modal
      active={!!selectedReport}
      callToAction={saveUsers}
      closeModal={resetAndCloseModal}
      loading={submitDisabled}
      absolute
      top="15vh"
      right="200px"
      width="868px"
      buttonNames={{
        action: t('viewSurvey.sharing.modal.save'),
        cancel: t('viewSurvey.sharing.modal.cancel'),
      }}
      title={title}
    >
      <Content>
        <SearchDropdown
          width="100%"
          items={items}
          placeholder={t('viewSurvey.sharing.modal.placeholder')}
          noResultsMessage={t('viewSurvey.sharing.modal.noResults')}
          searchMode
          doFilter={doFilter}
          selectEl={selectEl}
          selectFullObject
          label={t('viewSurvey.sharing.modal.shareWith')}
        />
        <Recipients>
          {selectedRecipients.map(item => {
            return (
              <Chip key={item?.id}>
                <ChipContent>
                  <ChipName>{item?.name || '-'}</ChipName>
                  <ChipEmail>
                    {item?.email}
                    {item?.shared && (
                      <EmailSent>
                        <SuccessNotificationIcon />
                        <EmailSentLabel>{t('viewSurvey.sharing.modal.shared')}</EmailSentLabel>
                      </EmailSent>
                    )}
                  </ChipEmail>
                </ChipContent>
                <DeleteItem
                  onClick={() => {
                    setSelectedRecipients(selectedRecipients.filter(rec => rec.id !== item?.id));
                  }}
                >
                  <CloseMark />
                </DeleteItem>
              </Chip>
            );
          })}
        </Recipients>
      </Content>
    </Modal>
  );
};

export default ShareResultsModal;
